"use client";

/**
 * CollabV3CheckinCard 컴포넌트
 * @description 상세의 체크인 카드(앱 `CollabDetailCheckinBlock`) — 스탬프 그림 옆에 `Check-in`, 체크인
 *   시간대, 체크인한 사람 수. 체크인은 현장에서 앱으로 한다(위치 · 코드 확인이 폰에 있다) — 웹은
 *   알려주기만 한다. 내 스탬프 줄은 로그인한 사람의 것이라 게스트인 웹에는 없다.
 * @example <CollabV3CheckinCard checkin={detail.checkin} />
 */
import { Card } from "@fanwork-web/design-system";
import { memo, useMemo } from "react";

import {
  COLLAB_V3_STAMP_WIDTH,
  collabV3CardStyles as styles,
} from "./CollabV3Card.styles";
import type { CollabV3CheckinCardProps } from "./CollabV3Card.types";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";

import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import { formatDayTime } from "../../utils/collabV3Format";
import { AppImage, IMAGE_PLACEHOLDER } from "@/shared/ui";

const copy = COLLAB_V3_CARD_COPY.checkin;

const CollabV3CheckinCard = memo<CollabV3CheckinCardProps>(({ checkin }) => {
  const timeZone = useCollabV3TimeZone();
  const window = useMemo(
    () =>
      copy.window(
        formatDayTime(checkin.startAt, timeZone),
        formatDayTime(checkin.endAt, timeZone),
      ),
    [checkin.startAt, checkin.endAt, timeZone],
  );

  return (
    <Card
      slot={
        <div className={styles.checkin.head}>
          <div className={styles.checkin.thumb}>
            <AppImage
              uri={checkin.stampImageUrl}
              fallback={IMAGE_PLACEHOLDER}
              targetWidth={COLLAB_V3_STAMP_WIDTH}
            />
          </div>
          <div className={styles.checkin.texts}>
            <h3 className={styles.title}>{copy.title}</h3>
            <p className={styles.body}>{window}</p>
            <p className={styles.meta}>{copy.count(checkin.checkinCount)}</p>
          </div>
        </div>
      }
    />
  );
});

CollabV3CheckinCard.displayName = "CollabV3CheckinCard";

export default CollabV3CheckinCard;
